import React, { useCallback } from 'react';
import { BarChart2, Server, Settings } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров блокчейн-проекта
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  projectTypes,
  blockchainPlatforms,
  projectSizes
}) => {
  // Обработчик изменения количества смарт-контрактов
  const handleSmartContractsChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      smartContracts: newValue
    }));
  };
  
  // Обработчик изменения срока проекта
  const handleDurationChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      projectDuration: newValue
    }));
  };
  
  // Функция расчета
  const calculateProject = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Функция для быстрой установки шаблона проекта
  const setProjectTemplate = useCallback((template) => {
    const newFormData = { ...formData };
    
    switch(template) {
      case 'token':
        newFormData.projectType = 'token';
        newFormData.projectSize = 'small';
        newFormData.smartContracts = '1';
        newFormData.smartContractsComplexity = 'low';
        newFormData.customTokenRequired = true;
        newFormData.frontendComplexity = 'low';
        newFormData.projectDuration = '2';
        break;
        
      case 'dapp':
        newFormData.projectType = 'dapp';
        newFormData.projectSize = 'medium';
        newFormData.smartContracts = '3';
        newFormData.smartContractsComplexity = 'medium';
        newFormData.frontendComplexity = 'medium';
        newFormData.projectDuration = '4';
        break;
        
      case 'defi':
        newFormData.projectType = 'defi';
        newFormData.projectSize = 'large';
        newFormData.smartContracts = '5';
        newFormData.smartContractsComplexity = 'high';
        newFormData.frontendComplexity = 'high';
        newFormData.securityAudit = true;
        newFormData.customTokenRequired = true;
        newFormData.includeAdditionalFeatures = true;
        newFormData.featuresMultiSig = true;
        newFormData.projectDuration = '6';
        break;
        
      case 'nft':
        newFormData.projectType = 'nft';
        newFormData.projectSize = 'medium';
        newFormData.smartContracts = '4';
        newFormData.smartContractsComplexity = 'medium';
        newFormData.frontendComplexity = 'high';
        newFormData.customUIRequirements = 'premium';
        newFormData.includeAdditionalFeatures = true;
        newFormData.featuresNFT = true;
        newFormData.projectDuration = '4';
        break;
        
      case 'enterprise':
        newFormData.projectType = 'private';
        newFormData.projectSize = 'enterprise';
        newFormData.blockchainPlatform = 'hyperledger';
        newFormData.smartContracts = '8';
        newFormData.smartContractsComplexity = 'high';
        newFormData.frontendComplexity = 'high';
        newFormData.securityAudit = true;
        newFormData.testingLevel = 'extensive';
        newFormData.projectDuration = '10';
        newFormData.maintenanceMonths = '12';
        break;
        
      default:
        break;
    }
    
    setFormData(newFormData);
  }, [formData, setFormData]);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка проекта
      </h3>
      
      <div className="space-y-5">
        {/* Шаблоны проектов */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Готовые шаблоны проектов
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              type="button"
              onClick={() => setProjectTemplate('token')}
              className="p-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
            >
              <div className="font-medium">Токен</div>
              <div className="opacity-75 truncate">$15-30K</div>
            </button>
            <button
              type="button"
              onClick={() => setProjectTemplate('dapp')}
              className="p-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
            >
              <div className="font-medium">Базовое DApp</div>
              <div className="opacity-75 truncate">$45-90K</div>
            </button>
            <button
              type="button"
              onClick={() => setProjectTemplate('defi')}
              className="p-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
            >
              <div className="font-medium">DeFi платформа</div>
              <div className="opacity-75 truncate">$100-250K</div>
            </button>
            <button
              type="button"
              onClick={() => setProjectTemplate('nft')}
              className="p-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
            >
              <div className="font-medium">NFT маркетплейс</div>
              <div className="opacity-75 truncate">$70-150K</div>
            </button>
            <button
              type="button"
              onClick={() => setProjectTemplate('enterprise')}
              className="p-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left col-span-2"
            >
              <div className="font-medium">Корпоративное решение</div>
              <div className="opacity-75 truncate">$250-500K+</div>
            </button>
          </div>
        </div>
      
        {/* Тип проекта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип проекта</label>
          </div>
          <select
            value={formData.projectType}
            onChange={(e) => setFormData(prev => ({ ...prev, projectType: e.target.value }))}
            className="form-select w-full"
          >
            {projectTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Блокчейн-платформа */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Блокчейн-платформа</label>
          </div>
          <select
            value={formData.blockchainPlatform}
            onChange={(e) => setFormData(prev => ({ ...prev, blockchainPlatform: e.target.value }))}
            className="form-select w-full"
          >
            {blockchainPlatforms.map(platform => (
              <option key={platform.id} value={platform.id}>
                {platform.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Размер проекта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Размер проекта</label>
          </div>
          <div className="grid grid-cols-4 gap-1 mt-2">
            {projectSizes.map(size => (
              <button
                key={size.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, projectSize: size.id }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.projectSize === size.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {size.id === 'small' ? 'Малый' : 
                 size.id === 'medium' ? 'Средний' : 
                 size.id === 'large' ? 'Крупный' : 'Корп.'}
              </button>
            ))}
          </div>
        </div>
        
        {/* Количество смарт-контрактов */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="smartContractsSlider" className="text-gray-600 dark:text-gray-400">Количество смарт-контрактов</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.smartContracts}</span>
          </div>
          <input
            type="range"
            id="smartContractsSlider"
            min="1"
            max="20"
            step="1"
            value={formData.smartContracts}
            onChange={handleSmartContractsChange}
            aria-label="Количество смарт-контрактов"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными значениями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['1', '3', '5', '10'].map(count => (
              <button
                key={count}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, smartContracts: count }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.smartContracts === count 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {count}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок проекта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="projectDurationSlider" className="text-gray-600 dark:text-gray-400">Срок разработки (месяцев)</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.projectDuration}</span>
          </div>
          <input
            type="range"
            id="projectDurationSlider"
            min="1"
            max="24"
            step="1"
            value={formData.projectDuration}
            onChange={handleDurationChange}
            aria-label="Срок разработки (месяцев)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['2', '4', '6', '12'].map(months => (
              <button
                key={months}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, projectDuration: months }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.projectDuration === months 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {months} мес.
              </button>
            ))}
          </div>
        </div>
        
        {/* Кастомный токен */}
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="customTokenQuick"
            checked={formData.customTokenRequired}
            onChange={(e) => setFormData(prev => ({ ...prev, customTokenRequired: e.target.checked }))}
            className="form-checkbox"
          />
          <label htmlFor="customTokenQuick" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Кастомный токен
          </label>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateProject}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать проект
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;